import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { X, Link2, Mail, Copy, Check, Lock, User } from 'lucide-react';
import toast from 'react-hot-toast';
import './ShareModal.css';

interface ShareModalProps {
  credentialId?: string;
  kubeconfigId?: string;
  vpnId?: string;
  credentialName?: string;
  kubeconfigName?: string;
  vpnName?: string;
  resourceType?: 'credential' | 'kubeconfig' | 'vpn';
  onClose: () => void;
  onSuccess: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({ 
  credentialId, 
  kubeconfigId,
  vpnId,
  credentialName, 
  kubeconfigName,
  vpnName,
  resourceType = credentialId ? 'credential' : (kubeconfigId ? 'kubeconfig' : 'vpn'),
  onClose, 
  onSuccess 
}) => {
  const { user } = useAuth();
  const resourceId = credentialId || kubeconfigId || vpnId || '';
  const resourceName = credentialName || kubeconfigName || vpnName || '';
  
  // For STANDARD_USER sharing credentials, only allow "Existing User" option
  const isStandardUserSharingCredential = user?.role === 'STANDARD_USER' && resourceType === 'credential';
  const defaultShareType = isStandardUserSharingCredential ? 'user' : 'link';
  
  const [shareType, setShareType] = useState<'link' | 'email' | 'user'>(defaultShareType);
  
  // Ensure STANDARD_USER can only use 'user' share type for credentials
  useEffect(() => {
    if (isStandardUserSharingCredential && shareType !== 'user') {
      setShareType('user');
    }
  }, [isStandardUserSharingCredential, shareType]);
  const [expiresInHours, setExpiresInHours] = useState(24);
  const [neverExpires, setNeverExpires] = useState(false);
  // Default: true for links, false for email (to prevent email client preview issues)
  const [isOneTimeUse, setIsOneTimeUse] = useState(true);
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [guestCredentials, setGuestCredentials] = useState<{ username: string; password: string; email: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  // Fetch users for sharing - use search endpoint that's accessible to all users
  // Only fetch when user is typing (has search term)
  const { data: usersData, isLoading: usersLoading } = useQuery(
    ['users-for-share', userSearch],
    async () => {
      if (!userSearch || userSearch.trim().length < 2) {
        return []; // Don't search if less than 2 characters
      }
      try {
        // Try the search endpoint first (accessible to all users)
        const response = await api.get(`/users/search?q=${encodeURIComponent(userSearch.trim())}`);
        return response.data.users || [];
      } catch (error: any) {
        // Fallback to regular users endpoint if search fails (for admins)
        if (error.response?.status === 404 || error.response?.status === 403) {
          try {
            const response = await api.get(`/users?search=${encodeURIComponent(userSearch.trim())}`);
            return response.data.users || [];
          } catch {
            return [];
          }
        }
        return [];
      }
    },
      {
        enabled: shareType === 'user' && Boolean(userSearch) && userSearch.trim().length >= 2,
        staleTime: 30000, // Cache for 30 seconds
      }
  );

  const users = usersData || [];
  // No need for client-side filtering - API handles it
  const filteredUsers = users;

  const createLinkShare = useMutation(
    async (data: any) => {
      let endpoint;
      if (resourceType === 'credential') {
        endpoint = `/shares/credentials/${resourceId}/share`;
      } else if (resourceType === 'kubeconfig') {
        endpoint = `/shares/kubeconfig/${resourceId}/share`;
      } else {
        endpoint = `/shares/vpn/${resourceId}/share`;
      }
      const response = await api.post(endpoint, data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setShareUrl(data.share.shareUrl);
        toast.success('Share link created successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to create share link');
      },
    }
  );

  const createEmailShare = useMutation(
    async (data: any) => {
      let endpoint;
      if (resourceType === 'credential') {
        endpoint = `/shares/credentials/${resourceId}/share/email`;
      } else if (resourceType === 'kubeconfig') {
        endpoint = `/shares/kubeconfig/${resourceId}/share/email`;
      } else {
        endpoint = `/shares/vpn/${resourceId}/share/email`;
      }
      const response = await api.post(endpoint, data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setShareUrl(data.share.shareUrl);
        setGuestCredentials(data.guestCredentials);
        toast.success('Email share created! Guest credentials generated.');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to create email share');
      },
    }
  );

  const shareWithUser = useMutation(
    async (data: any) => {
      let endpoint;
      if (resourceType === 'credential') {
        endpoint = `/shares/credentials/${resourceId}/share/user`;
      } else if (resourceType === 'kubeconfig') {
        endpoint = `/kubeconfig/${resourceId}/share/user`;
      } else {
        endpoint = `/vpn/${resourceId}/share/user`;
      }
      const response = await api.post(endpoint, data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        const resourceLabel = resourceType === 'credential' ? 'Credential' : (resourceType === 'kubeconfig' ? 'Kubeconfig' : 'VPN');
        toast.success(data.message || `${resourceLabel} shared successfully!`);
        onSuccess();
        onClose();
      },
      onError: (error: any) => {
        const resourceLabel = resourceType === 'credential' ? 'credential' : (resourceType === 'kubeconfig' ? 'kubeconfig' : 'VPN');
        toast.error(error.response?.data?.error || `Failed to share ${resourceLabel} with user`);
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (shareType === 'user') {
      if (!selectedUserId) {
        toast.error('Please select a user');
        return;
      }
      shareWithUser.mutate({
        userId: selectedUserId,
        canView: true,
        canEdit,
        canDelete,
      });
      return;
    }

    const data: any = {
      isOneTimeUse,
    };

    // Only include expiresInHours if not set to never expire
    if (!neverExpires) {
      data.expiresInHours = expiresInHours;
    }

    if (shareType === 'link' && password) {
      data.password = password;
    }

    if (shareType === 'email') {
      if (!email) {
        toast.error('Email is required');
        return;
      }
      data.email = email;
      createEmailShare.mutate(data);
    } else {
      createLinkShare.mutate(data);
    }
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard!`);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <Link2 size={24} />
            <h2>Share {resourceType === 'credential' ? 'Credential' : 'Kubeconfig'}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {!shareUrl ? (
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label>{resourceType === 'credential' ? 'Credential' : 'Kubeconfig'}</label>
              <input type="text" value={resourceName} disabled className="disabled-input" />
            </div>

            <div className="form-group">
              <label>Share Type *</label>
              {isStandardUserSharingCredential && (
                <div className="info-message" style={{ marginBottom: '10px', padding: '10px', background: '#e0e7ff', borderRadius: '6px', fontSize: '14px', color: '#3730a3' }}>
                  <strong>Note:</strong> Standard users can only share credentials with existing users.
                </div>
              )}
              <div className="share-type-buttons">
                <button
                  type="button"
                  className={`share-type-btn ${shareType === 'link' ? 'active' : ''} ${isStandardUserSharingCredential ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!isStandardUserSharingCredential) {
                      setShareType('link');
                      setIsOneTimeUse(true); // Default to one-time for direct links
                    }
                  }}
                  disabled={isStandardUserSharingCredential}
                  title={isStandardUserSharingCredential ? 'Not available for standard users' : 'Direct Link'}
                >
                  <Link2 size={20} />
                  <span>Direct Link</span>
                </button>
                <button
                  type="button"
                  className={`share-type-btn ${shareType === 'email' ? 'active' : ''} ${isStandardUserSharingCredential ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!isStandardUserSharingCredential) {
                      setShareType('email');
                      setIsOneTimeUse(false); // Default to reusable for email shares
                    }
                  }}
                  disabled={isStandardUserSharingCredential}
                  title={isStandardUserSharingCredential ? 'Not available for standard users' : 'Email Invitation'}
                >
                  <Mail size={20} />
                  <span>Email Invitation</span>
                </button>
                <button
                  type="button"
                  className={`share-type-btn ${shareType === 'user' ? 'active' : ''}`}
                  onClick={() => {
                    setShareType('user');
                  }}
                >
                  <User size={20} />
                  <span>Existing User</span>
                </button>
              </div>
            </div>

            {shareType === 'email' && (
              <div className="form-group">
                <label>Recipient Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="recipient@example.com"
                />
              </div>
            )}

            {shareType === 'user' && (
              <>
                <div className="form-group" style={{ position: 'relative' }}>
                  <label>Search User *</label>
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      if (!e.target.value) {
                        setSelectedUserId('');
                      }
                    }}
                    placeholder="Type at least 2 characters to search by email, username, or name..."
                    className="user-search-input"
                    autoComplete="off"
                  />
                  {userSearch && userSearch.trim().length >= 2 && (
                    <div className="user-dropdown">
                      {usersLoading ? (
                        <div className="user-dropdown-item">Searching...</div>
                      ) : filteredUsers.length === 0 ? (
                        <div className="user-dropdown-item" style={{ color: '#ef4444' }}>
                          No users found. Try a different search term.
                        </div>
                      ) : (
                        filteredUsers.slice(0, 10).map((user: any) => (
                          <div
                            key={user.id}
                            className={`user-dropdown-item ${selectedUserId === user.id ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedUserId(user.id);
                              setUserSearch(user.email || user.username);
                            }}
                          >
                            <div className="user-item-name">
                              {user.firstName || user.lastName
                                ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                                : user.username}
                            </div>
                            <div className="user-item-email">{user.email}</div>
                            <div className="user-item-role">{user.role?.replace('_', ' ')}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {userSearch && userSearch.trim().length < 2 && (
                    <div className="user-dropdown">
                      <div className="user-dropdown-item" style={{ color: '#64748b' }}>
                        Type at least 2 characters to search...
                      </div>
                    </div>
                  )}
                </div>

                {selectedUserId && (
                  <div className="form-group">
                    <label>Permissions</label>
                    <div className="permissions-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={true}
                          disabled
                        />
                        <span>View (always enabled)</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={canEdit}
                          onChange={(e) => setCanEdit(e.target.checked)}
                        />
                        <span>Edit</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={canDelete}
                          onChange={(e) => setCanDelete(e.target.checked)}
                        />
                        <span>Delete</span>
                      </label>
                    </div>
                    <small>The {resourceType === 'credential' ? 'credential' : 'kubeconfig'} will appear in the user's {resourceType === 'credential' ? 'credentials' : 'kubeconfigs'} list immediately.</small>
                  </div>
                )}
              </>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={neverExpires}
                  onChange={(e) => {
                    setNeverExpires(e.target.checked);
                  }}
                />
                <span>Never expire</span>
              </label>
              {!neverExpires && (
                <>
                  <label style={{ marginTop: '0.5rem', display: 'block' }}>Expires In (Hours)</label>
                  <input
                    type="number"
                    value={expiresInHours}
                    onChange={(e) => setExpiresInHours(parseInt(e.target.value) || 24)}
                    min={1}
                    max={720}
                    required
                  />
                  <small>Link will expire after {expiresInHours} hour(s)</small>
                </>
              )}
              {neverExpires && (
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b' }}>
                  This share will never expire (unless manually revoked)
                </small>
              )}
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isOneTimeUse}
                  onChange={(e) => setIsOneTimeUse(e.target.checked)}
                />
                <span>One-time use only</span>
              </label>
              <small>
                {isOneTimeUse 
                  ? 'Link will be disabled after first access (recommended for direct links, not recommended for email shares)'
                  : 'Link can be accessed multiple times until expiration (recommended for email shares)'}
              </small>
              {shareType === 'email' && isOneTimeUse && (
                <div className="warning-box" style={{ marginTop: '0.5rem', padding: '0.75rem' }}>
                  <strong>⚠️ Warning:</strong> One-time use links sent via email may be consumed by email client previews. 
                  Consider disabling one-time use for email shares.
                </div>
              )}
            </div>

            {shareType === 'link' && (
              <div className="form-group">
                <label>
                  <Lock size={16} />
                  Password Protection (Optional)
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Leave empty for no password"
                />
                <small>Require password to access shared {resourceType === 'credential' ? 'credential' : 'kubeconfig'}</small>
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={createLinkShare.isLoading || createEmailShare.isLoading || shareWithUser.isLoading}
              >
                {createLinkShare.isLoading || createEmailShare.isLoading || shareWithUser.isLoading
                  ? 'Sharing...'
                  : shareType === 'user'
                  ? 'Share Now'
                  : 'Create Share'}
              </button>
            </div>
          </form>
        ) : (
          <div className="share-result">
            <div className="success-message">
              <Check size={48} className="success-icon" />
              <h3>Share Created Successfully!</h3>
            </div>

            <div className="share-info">
              <div className="form-group">
                <label>Share Link</label>
                <div className="input-with-button">
                  <input type="text" value={shareUrl} readOnly className="share-url-input" />
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={() => handleCopy(shareUrl, 'Share link')}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              {guestCredentials && (
                <div className="guest-credentials">
                  <h4>Guest Credentials (Send these to the recipient)</h4>
                  <div className="form-group">
                    <label>Username</label>
                    <div className="input-with-button">
                      <input type="text" value={guestCredentials.username} readOnly />
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => handleCopy(guestCredentials.username, 'Username')}
                      >
                        {copied ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Password</label>
                    <div className="input-with-button">
                      <input type="text" value={guestCredentials.password} readOnly />
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => handleCopy(guestCredentials.password, 'Password')}
                      >
                        {copied ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="warning-box">
                    <strong>Important:</strong> Save these credentials! They won't be shown again.
                  </div>
                </div>
              )}

              <div className="share-details">
                <p><strong>Expires:</strong> {neverExpires ? 'Never' : new Date(Date.now() + expiresInHours * 60 * 60 * 1000).toLocaleString()}</p>
                <p><strong>One-time use:</strong> {isOneTimeUse ? 'Yes' : 'No'}</p>
                {password && <p><strong>Password protected:</strong> Yes</p>}
                {isOneTimeUse && (
                  <div className="warning-box" style={{ marginTop: '1rem' }}>
                    <strong>⚠️ Important:</strong> Do not test this link yourself! One-time use links are disabled after first access. 
                    Send it directly to the recipient.
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareModal;

